import { CLOSED_RESULT_LABEL, type BoardCardDTO, type ClosedResult, type MissionStatus } from "@bgreen/shared";
import { Hand, Lock, Pause, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/cn";
import { rememberOpened, span, STATE_SHORT } from "../../lib/board";
import { PRIORITY_LABEL } from "../../lib/labels";
import { Chip, Dot } from "../ui/primitives";

const PRIORITY_COLOR = { high: "er", medium: "wn", low: "mu" } as const;
const STATE_COLOR: Record<MissionStatus, string> = {
  pending: "mu", planning: "in", awaiting_plan_approval: "wn", developing: "in", awaiting_ci: "in", in_review: "wn", merging: "in", done: "gd", blocked: "wn", error: "er", awaiting_budget: "wn", cancelled: "mu",
};
export const RESULT_TONE: Record<ClosedResult, "good" | "bad" | "neutral" | "warn"> = { completed: "good", rejected: "bad", cancelled: "neutral", closed_unmerged: "warn" };

/** Cuándo se cerró: la fecha efectiva de finalización, o la aclaración de que no consta (nunca una fecha inventada). */
export const closedAgo = (c: BoardCardDTO) => (c.closedAt ? `hace ${span(c.closedAt)}` : "sin fecha de cierre");

export const repoShort = (full: string) => full.split("/").pop() ?? full;

export function PriorityMark({ p }: { p: BoardCardDTO["priority"] }) {
  return <span className="inline-flex items-center gap-1.5 text-[12px] text-mu"><Dot color={PRIORITY_COLOR[p]} />{PRIORITY_LABEL[p]}</span>;
}

/** Los tres tiempos que no deben confundirse: desde la creación, última actividad y tiempo en la fase actual. */
export function timesTitle(c: BoardCardDTO): string {
  const t = (iso: string | null) => span(iso) ?? "sin dato fiable";
  const when = (iso: string) => new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  return c.result
    ? `Creada hace ${t(c.createdAt)} · ${c.closedAt ? `Cerrada el ${when(c.closedAt)} (hace ${t(c.closedAt)})` : "Sin fecha de cierre registrada"} · Última actividad hace ${t(c.lastActivityAt)}`
    : `Creada hace ${t(c.createdAt)} · Última actividad hace ${t(c.lastActivityAt)} · En esta fase: ${c.phaseSince ? `${t(c.phaseSince)}` : "sin dato fiable"}`;
}

/** Aviso de pausa (motivo breve) o de acción requerida; el detalle completo está en la misión. */
function Attention({ c }: { c: BoardCardDTO }) {
  const paused = c.indicator === "blocked" || c.indicator === "error" || c.indicator === "awaiting_budget";
  if (!paused && !c.intervention) return null;
  const Icon = c.indicator === "error" ? TriangleAlert : Pause;
  return (
    <div className={cn("flex flex-col gap-1 rounded-lg border px-2.5 py-2 text-[12.5px] leading-snug", paused ? (c.indicator === "error" ? "border-er/50 bg-er/5" : "border-wn/50 bg-wn/5") : "border-ln bg-hv")}>
      {paused && (
        <div className={cn("flex items-start gap-1.5 font-medium", c.indicator === "error" ? "text-er" : "text-wn")}>
          <Icon className="mt-[2px] h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0"><span>{STATE_SHORT[c.status]}</span>{c.stateSince && <span className="font-normal opacity-80"> · hace {span(c.stateSince)}</span>}</span>
        </div>
      )}
      {paused && c.reason && <div className="line-clamp-2 text-mu" title={c.reason}>{c.reason}</div>}
      {c.intervention && (
        <div className={cn("flex items-start gap-1.5", c.intervention.canAct ? "font-medium text-tx" : "text-mu")} title={c.intervention.canAct ? "Puedes hacerlo con tu rol" : "Tu rol no permite esta acción: la tiene que hacer otra persona"}>
          {c.intervention.canAct ? <Hand className="mt-[2px] h-3.5 w-3.5 shrink-0 text-ac" aria-hidden /> : <Lock className="mt-[2px] h-3.5 w-3.5 shrink-0" aria-hidden />}
          <span className="min-w-0">{c.intervention.label}{!c.intervention.canAct && <span className="font-normal"> · otro rol</span>}</span>
        </div>
      )}
    </div>
  );
}

export function BoardCard({ c, selected, className }: { c: BoardCardDTO; selected?: boolean; className?: string }) {
  const closed = !!c.result;
  const paused = c.indicator === "blocked" || c.indicator === "error" || c.indicator === "awaiting_budget";
  const inPhase = span(c.phaseSince);
  return (
    <Link to={`/missions/${c.id}`} data-mission-id={c.id} onClick={() => rememberOpened(c.id)} title={`${c.companyName} / ${c.projectName} · ${c.repositoryName}`}
      className={cn("flex flex-col gap-2 rounded-card border bg-cd p-3 text-tx no-underline transition-colors hover:border-ln2 focus-visible:outline-2 focus-visible:outline-ac",
        selected ? "border-ac ring-2 ring-ac/40" : "border-ln", c.intervention?.canAct && !closed && "border-l-[3px] border-l-ac", className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[12px] text-mu">{c.key}</span>
        <PriorityMark p={c.priority} />
      </div>
      <div className="line-clamp-2 text-[14.5px] font-medium leading-snug" title={c.title}>{c.title}</div>
      <div className="truncate font-mono text-[12px] text-mu">{repoShort(c.repositoryName)}</div>
      <div className="flex items-center justify-between gap-2 text-[12.5px]">
        {closed ? (
          <Chip tone={RESULT_TONE[c.result!]} className="h-6 px-2 text-[12px]">{CLOSED_RESULT_LABEL[c.result!]}</Chip>
        ) : (
          <span className="inline-flex min-w-0 items-center gap-1.5"><Dot color={STATE_COLOR[c.status]} pulse={c.agentRunning} /><span className="truncate" title={c.agentRunning ? "Agente en ejecución" : undefined}>{paused ? "Pausada" : STATE_SHORT[c.status]}</span></span>
        )}
        <span className="shrink-0 font-mono text-[12px] text-mu" title={timesTitle(c)}>
          {closed ? closedAgo(c) : inPhase ? `${inPhase} en fase` : `act. ${span(c.lastActivityAt)}`}
        </span>
      </div>
      {closed && c.reason && <div className="line-clamp-2 text-[12.5px] text-mu" title={c.reason}>{c.reason}</div>}
      {!closed && <Attention c={c} />}
    </Link>
  );
}
